import React, { useState, useEffect } from "react";
import { AlertCircle, Eye, X } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import { listFinanceInvoices, showFinanceInvoice } from "../../services/adminFinance";

const STATUS_MAP = { open: { label: "Aberta", color: "pendente" }, overdue: { label: "Vencida", color: "inativo" }, paid: { label: "Paga", color: "ativo" }, cancelled: { label: "Cancelada", color: "default" } };

const formatCents = (c) => c !== null && c !== undefined ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(c) / 100) : "—";

const Invoices = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ status: "" });
  const [detail, setDetail] = useState(null);

  useEffect(() => { load(1); }, []);

  const load = async (page = 1) => {
    setLoading(true); setError("");
    try {
      const q = { pageNumber: page, pageSize: 20, sortBy: "createdAt", sortOrder: "DESC" };
      if (filters.status) q.status = filters.status;
      const data = await listFinanceInvoices(q);
      setItems(data.items || []);
      setPagination(data.pagination || {});
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const openDetail = async (row) => {
    try { const data = await showFinanceInvoice(row.id); setDetail(data); }
    catch (err) { /* */ }
  };

  const columns = [
    { header: "Empresa", render: (r) => <span className="font-semibold text-gray-900 dark:text-gray-100">{r.companyName || `#${r.companyId}`}</span> },
    { header: "Valor", render: (r) => <span className="font-bold">{formatCents(r.amountCents)}</span> },
    { header: "Status", render: (r) => { const s = STATUS_MAP[r.status] || { label: r.status, color: "default" }; return <StatusBadge status={s.color} label={s.label} />; } },
    { header: "Vencimento", render: (r) => r.dueDate ? <span className={`text-xs ${r.status === "overdue" ? "text-rose-600 font-bold" : ""}`}>{new Date(r.dueDate).toLocaleDateString("pt-BR")}</span> : <span className="text-xs text-gray-400">—</span> },
    { header: "Pago em", render: (r) => r.paidAt ? <span className="text-xs text-emerald-600">{new Date(r.paidAt).toLocaleDateString("pt-BR")}</span> : <span className="text-xs text-gray-400">—</span> },
    { header: "Tipo", render: (r) => <span className="text-xs">{r.invoiceType}</span> },
    { header: "Ações", render: (r) => <button onClick={() => openDetail(r)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400"><Eye size={15} /></button> }
  ];

  return (
    <div className="space-y-4">
      <PageHeader breadcrumb="Financeiro" title="Faturas" subtitle={`${pagination.total || 0} fatura(s)`} />
      {error && <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3"><AlertCircle size={16} /> {error}</div>}
      <div className="flex gap-2">
        <select value={filters.status} onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))} className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs focus:outline-none focus:border-purple-500">
          <option value="">Todos os status</option>
          <option value="open">Abertas</option>
          <option value="overdue">Vencidas</option>
          <option value="paid">Pagas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <button onClick={() => load(1)} className="px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">Filtrar</button>
      </div>
      <DataTable columns={columns} data={items} loading={loading} emptyTitle="Nenhuma fatura" />
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-lg p-6 border border-gray-200 dark:border-white/10 space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Fatura #{detail.id}</h3>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-gray-500">Empresa:</span> {detail.company?.name || `#${detail.companyId}`}</div>
              <div><span className="text-gray-500">Valor:</span> <span className="font-bold">{formatCents(detail.amountCents)}</span></div>
              <div><span className="text-gray-500">Status:</span> <StatusBadge status={(STATUS_MAP[detail.status] || {}).color || "default"} label={detail.status} /></div>
              <div><span className="text-gray-500">Tipo:</span> {detail.invoiceType}</div>
              {detail.dueDate && <div><span className="text-gray-500">Vencimento:</span> {new Date(detail.dueDate).toLocaleDateString("pt-BR")}</div>}
              {detail.paidAt && <div><span className="text-gray-500">Pago:</span> {new Date(detail.paidAt).toLocaleString("pt-BR")}</div>}
              {detail.gatewayInvoiceId && <div><span className="text-gray-500">Gateway ID:</span> {detail.gatewayInvoiceId}</div>}
              {detail.dataQuality && detail.dataQuality !== "ok" && <div className="col-span-2"><span className="text-amber-600 font-semibold">⚠ Qualidade dos dados: {detail.dataQuality}</span></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
